import Vue from 'vue';
import VueRouter, { Route } from 'vue-router';
import { ref, Ref } from '@vue/composition-api';

Vue.use(VueRouter);

import { authorize } from './router-utils';
import { getStore, setThemeDark } from './store/index';
import { init as initI18n } from './i18n';

import Register from './pages/Register.vue';
import RegisterConfirm from './pages/RegisterConfirm.vue';
import SignIn from './pages/SignIn.vue';
import Sharing from './pages/Sharing.vue';
import Consent from './pages/Consent.vue';
import PasswordRecovery from './pages/PasswordRecovery.vue';
import PasswordUpdate from './pages/PasswordUpdate.vue';
import PasswordUpdateConfirm from './pages/PasswordUpdateConfirm.vue';
import UserDashboard from './pages/UserDashboard.vue';
import UserDelete from './pages/UserDelete.vue';
import PrivacyPolicy from './pages/privacy/PrivacyPolicy.vue';
import VueI18n from 'vue-i18n';

const basePath = '/';

const routes = [
    {
        path: basePath,
        redirect: 'access/signin'
    },
    {
        path: basePath + 'access/register',
        name: 'access-register',
        component: Register,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessRegisterHtmlTitle')
        },
    },
    {
        path: basePath + 'access/email-confirm',
        name: 'access-email-confirm',
        component: RegisterConfirm,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessRegisterConfirmHtmlTitle')
        }
    },
    {
        path: basePath + 'access/signin',
        name: 'access-signin',
        component: SignIn,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessSignInHtmlTitle')
        },
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();

            const authorized = await store.dispatch('authenticationCheck');

            if (authorized) {
                next({ name: 'user-dashboard' });
            }
            else {
                next();
            }
        },
    },
    {
        path: basePath + 'access/consent',
        name: 'access-consent',
        component: Consent,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessConsentHtmlTitle')
        }
    },
    {
        path: basePath + 'access/password-recovery',
        name: 'access-password-recovery',
        component: PasswordRecovery,
        meta: {
            treeLevel: 1,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessPasswordRecoveryHtmlTitle')
        }
    },
    {
        path: basePath + 'access/password-update',
        name: 'access-password-update',
        component: PasswordUpdate,
        meta: {
            treeLevel: 1,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessPasswordUpdateHtmlTitle')
        }
    },
    {
        path: basePath + 'access/password-update/confirm',
        name: 'access-password-update-confirm',
        component: PasswordUpdateConfirm,
        meta: {
            treeLevel: 2,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessPasswordUpdateHtmlTitle')
        }
    },
    {
        path: basePath + 'privacy-policy',
        name: 'privacy-policy',
        component: PrivacyPolicy,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessPrivacyPolicyHtmlTitle')
        }
    },
    {
        path: basePath + 'user/dashboard',
        name: 'user-dashboard',
        component: UserDashboard,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessUserDashboardHtmlTitle'),
            authorize: true,
        },
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getUserPlants', { token: to.query.token })) {
                next();
            }
            else {
                next()
            }
        },
    },
    {
        path: basePath + 'user/dashboard/delete',
        name: 'user-delete',
        component: UserDelete,
        meta: {
            treeLevel: 1,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessUserDeleteHtmlTitle'),
            authorize: true,
        },
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();

            if (!store.state.userPlants) {
                if (await store.dispatch('getUserPlants')) {
                    next();
                }
            }
            else {
                next();
            }
        },
    },
    {
        path: basePath + 'sharing/',
        name: 'sharing',
        component: Sharing,
        meta: {
            treeLevel: 0,
            htmlTitle: (i18n: VueI18n) => i18n.t('accessSharingHtmlTitle')
        },

        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            const { route } = useRouter();

            store.commit('setClientApp', route.value.query.client ?? 'com.findernet.You');

            await store.dispatch('verifyTokenInfo', { token: route.value.query.c })
            next();
        },
    },
]

let router: VueRouter, route: Ref<Route>;

export function init() {
    let store = getStore();

    if (router == null) {
        router = new VueRouter({
            mode: 'history',
            scrollBehavior(to, from, savedPosition) {
                if (savedPosition) {
                    return savedPosition;
                }
                else {
                    return { x: 0, y: 0 };
                }
            },
            routes
        });

        router.beforeEach(async (to: Route, from: Route, next: Function) => {
            const i18n = initI18n();
            document.title = to.meta.htmlTitle ? `Finder Accounts - ${to.meta.htmlTitle(i18n)}` : 'Finder Accounts';

            route.value = to;

            const authorize = to.matched.filter(x => !!x.meta.authorize).map(x => x.meta.authorize);

            if (authorize.length > 0) {
                let authorized = false;

                if (!authorized) {
                    authorized = await store.dispatch('authenticationCheck', null);
                }

                if (!authorized) {
                    next({ name: 'access-signin' });
                    return;
                }

                if (authorized && to.matched.filter(x => !!x.meta.access).length > 0) {
                    next();
                    return;
                }
            }

            next();
        });

        route = ref<Route>(null);
    }

    return router;
}

export function useRouter() {
    return { router, route }
}

